import { Pagination } from "services/search";

import { DataWithPagination } from "services/api/types";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformContractRentTemplate, transformContractRentTemplateOnCreate, transformContractRentTemplateOnUpdate, transformContractRentTemplateRow, transformContractRentTemplateTag, transformContractSaleTemplate, transformContractSaleTemplateOnCreate, transformContractSaleTemplateOnUpdate, transformContractSaleTemplateRow, transformContractSaleTemplateTag } from "./transformation";
import { transformErrorResponse } from "services/api/transformation";
import { TemplateTag } from "ui/components/TemplateEditor/types";
import { ContractRentTemplate, ContractRentTemplateFormValues, ContractRentTemplateRow, ContractSaleTemplate, ContractSaleTemplateFormValues, ContractSaleTemplateRow } from "./types";



/*---------------------------------------------------------------------------------------
  DOCUMENTS - CONTRACT - SALE - TEMPLATE 
-----------------------------------------------------------------------------------------*/
export const getContractSaleTemplatesAPI = async (
  pagination: Pagination,
): Promise<DataWithPagination<ContractSaleTemplateRow>> => {

  let path = `/v1/settings/contracts/sale/templates`;

  return await paginatedApiCall(
    path,
    pagination,
    transformContractSaleTemplateRow
  );
};

/*---------------------------------------------------------------------------------------
   DOCUMENTS - CONTRACT - SALE - TEMPLATE - GET BY ID
-----------------------------------------------------------------------------------------*/

export const getContractSaleTemplateAPI = async (id: string): Promise<ContractSaleTemplate> => {
  const response = await createApiCall(
    {
      path: `/v1/settings/contracts/sale/template/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContractSaleTemplate(response.data);
};

/*-----------------------------------------------------------------------------------------
       DOCUMENTS - CONTRACT - SALE - TEMPLATE - CREATE
-----------------------------------------------------------------------------------------*/
export const createContractSaleTemplateAPI = async (
  formValues: ContractSaleTemplateFormValues,
  t: (key: string) => string
): Promise<ContractSaleTemplate> => {
  const response = await createApiCall(
    {
      path: "/v1/settings/contracts/sale/template",
      method: "POST",
      body: transformContractSaleTemplateOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => t("admin.setting.contract.sale.template.message.create"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformContractSaleTemplate(response.data);
};

/*---------------------------------------------------------------------------------------
       DOCUMENTS - CONTRACT - SALE -  TEMPLATE - EDIT
-----------------------------------------------------------------------------------------*/
export const updateContractSaleTemplateAPI = async (
  id: string,
  formValues: ContractSaleTemplateFormValues,
  t: (key: string) => string
): Promise<ContractSaleTemplate> => {
  const response = await createApiCall(
    {
      path: `/v1/settings/contracts/sale/template/${id}`,
      method: "PUT",
      body: transformContractSaleTemplateOnUpdate(formValues, id),
    },
    {
      getMessage: {
        success: () => t("admin.setting.contract.sale.template.message.update"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformContractSaleTemplate(response.data);
};


/*---------------------------------------------------------------------------------------
    DELETE
-----------------------------------------------------------------------------------------*/

export const deleteContractSaleTemplateAPI = async (
  templateId: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/settings/contracts/sale/template/${templateId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.setting.contract.sale.template.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};


/*---------------------------------------------------------------------------------------
     DOCUMENTS - CONTRACT - SALE -  TAGS
-----------------------------------------------------------------------------------------*/
export const getContractSaleTemplateTagsAPI = async (
): Promise<TemplateTag[]> => {
  const response = await createApiCall(
    {
      path: `/v1/settings/contracts/sale/template/tags`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data.map(transformContractSaleTemplateTag);
};


/*---------------------------------------------------------------------------------------
  DOCUMENTS - CONTRACT - RENT - TEMPLATE
-----------------------------------------------------------------------------------------*/
export const getContractRentTemplatesAPI = async (
  pagination: Pagination,
): Promise<DataWithPagination<ContractRentTemplateRow>> => {

  let path = `/v1/settings/contracts/rent/templates`;

  return await paginatedApiCall(
    path,
    pagination,
    transformContractRentTemplateRow
  );
};

/*---------------------------------------------------------------------------------------
   DOCUMENTS - CONTRACT - RENT - TEMPLATE - GET BY ID
-----------------------------------------------------------------------------------------*/

export const getContractRentTemplateAPI = async (id: string): Promise<ContractRentTemplate> => {
  const response = await createApiCall(
    {
      path: `/v1/settings/contracts/rent/template/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContractRentTemplate(response.data);
};

/*-----------------------------------------------------------------------------------------
       DOCUMENTS - CONTRACT - RENT - TEMPLATE - CREATE
-----------------------------------------------------------------------------------------*/
export const createContractRentTemplateAPI = async (
  formValues: ContractRentTemplateFormValues,
  t: (key: string) => string
): Promise<ContractRentTemplate> => {
  const response = await createApiCall(
    {
      path: "/v1/settings/contracts/rent/template",
      method: "POST",
      body: transformContractRentTemplateOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => t("admin.setting.contract.rent.template.message.create"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformContractRentTemplate(response.data);
};

/*---------------------------------------------------------------------------------------
       DOCUMENTS - CONTRACT - RENT -  TEMPLATE - EDIT
-----------------------------------------------------------------------------------------*/
export const updateContractRentTemplateAPI = async (
  id: string,
  formValues: ContractRentTemplateFormValues,
  t: (key: string) => string
): Promise<ContractRentTemplate> => {
  const response = await createApiCall(
    {
      path: `/v1/settings/contracts/rent/template/${id}`,
      method: "PUT",
      body: transformContractRentTemplateOnUpdate(formValues, id),
    },
    {
      getMessage: {
        success: () => t("admin.setting.contract.rent.template.message.update"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformContractRentTemplate(response.data);
};


/*---------------------------------------------------------------------------------------
    DELETE
-----------------------------------------------------------------------------------------*/

export const deleteContractRentTemplateAPI = async (
  templateId: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/settings/contracts/rent/template/${templateId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.setting.contract.rent.template.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};


/*---------------------------------------------------------------------------------------
     DOCUMENTS - CONTRACT - RENT -  TAGS
-----------------------------------------------------------------------------------------*/
export const getContractRentTemplateTagsAPI = async (
): Promise<TemplateTag[]> => {
  const response = await createApiCall(
    {
      path: `/v1/settings/contracts/rent/template/tags`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data.map(transformContractRentTemplateTag);
};